import React, { useEffect, useState } from "react";
import CardEmpty from "Utils/Cards/CardEmpty";
import { NotificationManager } from "react-notifications";
import DatePicker from "react-datepicker";
import AsyncSelect from "react-select/lib/Async";
import { ResumenCierresApi } from "./api/ResumenCierresApi";
import LoadMask from "../../../../../../Utils/LoadMask/LoadMask";
import { InventariosHistoricosTable } from "./components/InventariosTable";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";
import moment from "moment";
import { ResumenReporte } from "./components/ResumenReporte";
import { PrintContainer } from "Utils/Print";
import { useDebouncedAsyncSelect } from "../../../../../../Utils/Hooks/useDebounce";

const STORAGE_KEY = "resumen_cierres_inventario";

export const ResumenCierresTab = () => {
    const filtrosGuardados =
        JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};

    const [inventarios, setInventarios] = useState([]);
    const [totalGlobal, setTotalGlobal] = useState(undefined);

    const {
        getReporteInventarios,
        obtenerReporteExcel,
        getPadresProductoTerminado,
        cargando,
    } = ResumenCierresApi();

    const [fechaInicial, setFechaInicial] = useState(
        filtrosGuardados.fecha_inicial
            ? moment(filtrosGuardados.fecha_inicial)
            : null,
    );
    const [fechaFinal, setFechaFinal] = useState(
        filtrosGuardados.fecha_final
            ? moment(filtrosGuardados.fecha_final)
            : null,
    );
    const [padrePT, setPadrePT] = useState(
        filtrosGuardados.padre_producto_terminado || null,
    );

    // se guardan los filtros para conservarlos al volver a la pestaña; al
    // entrar, la consulta inicial ya los usa
    useEffect(() => {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                fecha_inicial: fechaInicial,
                fecha_final: fechaFinal,
                padre_producto_terminado: padrePT,
            }),
        );
    }, [fechaInicial, fechaFinal, padrePT]);

    const loadPadresProductoTerminado = useDebouncedAsyncSelect(function (
        search,
    ) {
        return getPadresProductoTerminado({ search: search });
    });

    // las fechas se mandan tal como las eligió el usuario: el backend es el que
    // sabe que el cierre guarda su fecha_final un día después del corte
    const construirParams = (avisarSiFaltan) => {
        if (!fechaInicial || !fechaFinal) {
            if (avisarSiFaltan) {
                NotificationManager.warning(
                    "Por favor, selecciona una fecha inicial y una fecha final para filtrar el reporte.",
                    "Fechas incompletas",
                    3000,
                );
            }
            return null;
        }

        const params = {
            fecha_inicial: moment(fechaInicial).format("YYYY-MM-DD"),
            fecha_final: moment(fechaFinal).format("YYYY-MM-DD"),
        };

        if (padrePT) params.padre_producto_terminado_id = padrePT.id;

        return params;
    };

    const GetReporteInventarios = async (validarParams = false) => {
        try {
            const params = construirParams(validarParams);

            if (!params) return;

            const response = await getReporteInventarios(params);
            if (response) {
                setInventarios(response.inventarios || []);
                setTotalGlobal(response.total_global || {});
            }
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener inventarios disponibles",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setInventarios([]);
        }
    };

    useEffect(() => {
        GetReporteInventarios();
    }, []);

    const ObtenerReporteExcel = async () => {
        try {
            const params = construirParams(true);

            if (!params) return;

            await obtenerReporteExcel(params);
        } catch (error) {
            console.log("Error al obtener reporte Excel:", error);
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener el reporte en Excel",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const getReporteHeader = () => {
        let header = "Reporte de Inventarios";
        let dateRange = "";
        if (fechaInicial && fechaFinal) {
            dateRange = `${moment(fechaInicial).format("DD/MM/YYYY")} - ${moment(
                fechaFinal,
            ).format("DD/MM/YYYY")}`;
        }
        return (
            <div className="only-print">
                <div className="d-flex justify-content-between align-items-center">
                    <h4 className="m-0">{header}</h4>
                    {dateRange && (
                        <span className="gris-total text-sm">{dateRange}</span>
                    )}
                </div>
            </div>
        );
    };

    const hayInventarios = inventarios && inventarios.length > 0;

    return (
        <div className="reporte-inventarios__tab-wrapper">
            <CardEmpty noShadow>
                <LoadMask loading={cargando} blur>
                    <div className="reporte-inventarios__table-header">
                        <div className="formulario-fields-row">
                            <div className="formulario-field">
                                <label htmlFor="fecha_inicial" className="m-0">
                                    Fecha inicial
                                </label>
                                <DatePicker
                                    selected={fechaInicial}
                                    onChange={(date) => {
                                        setFechaInicial(date);
                                    }}
                                    showPopperArrow={false}
                                    dateFormat="DD/MM/YYYY"
                                    placeholderText="Fecha inicial"
                                    maxDate={fechaFinal || moment()}
                                />
                            </div>
                            <div className="formulario-field">
                                <label htmlFor="fecha_inicial" className="m-0">
                                    Fecha final
                                </label>
                                <DatePicker
                                    selected={fechaFinal}
                                    onChange={(date) => {
                                        setFechaFinal(date);
                                    }}
                                    showPopperArrow={false}
                                    dateFormat="DD/MM/YYYY"
                                    placeholderText="Fecha final"
                                    minDate={fechaInicial || undefined}
                                />
                            </div>
                            <div className="formulario-field">
                                <label
                                    htmlFor="padre_producto_terminado"
                                    className="m-0 text-nowrap"
                                >
                                    Padre producto terminado
                                </label>
                                <AsyncSelect
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    defaultOptions
                                    loadOptions={loadPadresProductoTerminado}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nombre}
                                    value={padrePT}
                                    onChange={(selected) =>
                                        setPadrePT(selected)
                                    }
                                    placeholder="Todos"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 20,
                                        }),
                                    }}
                                />
                            </div>
                            <button
                                className="btn btn-primary mt-auto"
                                onClick={() => GetReporteInventarios(true)}
                                disabled={!fechaInicial || !fechaFinal}
                            >
                                Filtrar
                            </button>
                        </div>
                        <div className="ml-auto formulario-fields-row flex-nowrap">
                            <div className="formulario-field">
                                <button
                                    className="btn btn-secondary mt-auto text-nowrap"
                                    onClick={ObtenerReporteExcel}
                                    disabled={!hayInventarios}
                                >
                                    Exportar Excel
                                </button>
                            </div>
                        </div>
                    </div>

                    <PrintContainer name="reporte-inventarios">
                        {getReporteHeader()}
                        {hayInventarios && (
                            <ResumenReporte totalGlobal={totalGlobal} />
                        )}

                        <InventariosHistoricosTable inventarios={inventarios} />
                    </PrintContainer>
                </LoadMask>
            </CardEmpty>
        </div>
    );
};
